import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    FaFire, FaLeaf, FaBoxOpen, FaIndustry, FaCheckCircle,
    FaShippingFast, FaCertificate, FaArrowLeft, FaEnvelope
} from "react-icons/fa";
import useDocumentMeta from "../hooks/useDocumentMeta";

// Product icons mapping
const productIcons = {
    "hardwood-charcoal": FaFire,
    "restaurant-hardwood-charcoal": FaLeaf,
    "premium-briquettes": FaBoxOpen,
    "industrial-lump": FaIndustry
};

// Product images mapping
const productImages = {
    "hardwood-charcoal": [
        "/assets/images/prod10.jpg",
        "/assets/images/prod4.jpeg",
        "/assets/images/gallery-10.jpg"
    ],
    "restaurant-hardwood-charcoal": [
        "/assets/images/prod9.png",
        "/assets/images/prod3.jpeg",
        "/assets/images/gallery-14.jpg"
    ],
};

export default function ProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [selectedImage, setSelectedImage] = useState(0);

    const productKeyMap = {
        "hardwood-charcoal": "hardwood",
        "restaurant-hardwood-charcoal": "coconut",
        "premium-briquettes": "briquettes",
        "industrial-lump": "industrial"
    };

    const product = {
        id: productId,
        name: t(`productDetail.products.${productId}.name`),
        tagline: t(`productDetail.products.${productId}.tagline`),
        icon: productIcons[productId],
        images: productImages[productId],
        cardDescription: t(`products.${productKeyMap[productId]}.description`),
        description: t(`productDetail.products.${productId}.description`),
        features: t(`productDetail.products.${productId}.features`, { returnObjects: true }),
        specifications: t(`productDetail.products.${productId}.specifications`, { returnObjects: true }),
        packaging: t(`productDetail.products.${productId}.packaging`, { returnObjects: true }),
        minOrder: t(`productDetail.products.${productId}.minOrder`),
        leadTime: t(`productDetail.products.${productId}.leadTime`),
        certifications: t(`productDetail.products.${productId}.certifications`, { returnObjects: true })
    };

    useDocumentMeta({
        title: t("seo.productDetail.title", { product: product.name }),
        description: product.description,
        canonicalPath: `/products/${productId}`,
        robots: "index,follow,max-image-preview:none",
    });

    if (!product || !product.name || product.name === `productDetail.products.${productId}.name`) {
        return (
            <main className="bg-vsblack text-white min-h-screen pt-20 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">{t('productDetail.notFound')}</h1>
                    <Link to="/products" className="btn-primary">
                        {t('productDetail.backToProducts')}
                    </Link>
                </div>
            </main>
        );
    }

    const ProductIcon = product.icon;

    return (
        <main className="bg-vsblack text-white min-h-screen pt-20">
            {/* Back Button */}
            <section className="py-6 px-6 border-b border-gray-800/50">
                <div className="max-w-7xl mx-auto">
                    <button
                        onClick={() => navigate('/products')}
                        className="flex items-center gap-2 text-gray-400 hover:text-vsred-light transition-colors"
                    >
                        <FaArrowLeft />
                        <span>{t('productDetail.backToProducts')}</span>
                    </button>
                </div>
            </section>

            {/* Product Hero */}
            <section className="py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Image Gallery */}
                        <motion.div
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            {/* Main Image */}
                            <div className="card-dark overflow-hidden mb-4">
                                <img
                                    src={product.images[selectedImage]}
                                    alt={product.name}
                                    className="w-full h-[600px] object-cover"
                                />
                            </div>

                            {/* Thumbnail Gallery */}
                            <div className="grid grid-cols-3 gap-4">
                                {product.images.map((img, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setSelectedImage(index)}
                                        className={`relative overflow-hidden rounded-lg transition-all duration-300 ${selectedImage === index
                                            ? "ring-2 ring-vsred shadow-red-glow"
                                            : "opacity-60 hover:opacity-100"
                                            }`}
                                    >
                                        <img
                                            src={img}
                                            alt={`${product.name} ${index + 1}`}
                                            className="w-full h-24 object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </motion.div>

                        {/* Product Info */}
                        <motion.div
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-vsred to-vsred-dark rounded-xl flex items-center justify-center shadow-red-glow">
                                    <ProductIcon className="text-3xl text-white" />
                                </div>
                                <div>
                                    <h1 className="text-4xl font-bold font-serif text-vsred">
                                        {product.name}
                                    </h1>
                                    <p className="text-gray-400 text-lg">{product.tagline}</p>
                                </div>
                            </div>

                            <p className="text-gray-300 leading-relaxed mb-6 text-lg">
                                {product.cardDescription}
                            </p>

                            {/* Key Features - inline under description */}
                            <div className="mb-6">
                                <h3 className="text-xl font-bold font-serif mb-4 text-vsred-light">{t('productDetail.keyFeatures')}</h3>
                                <div className="space-y-2">
                                    {product.features.map((feature, index) => (
                                        <div key={index} className="flex items-center gap-2 text-gray-300">
                                            <FaCheckCircle className="text-vsred flex-shrink-0" />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* CTA Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a
                                    href="/#quote"
                                    className="btn-primary flex-1 text-center flex items-center justify-center gap-2"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        navigate('/');
                                        setTimeout(() => {
                                            document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                                        }, 100);
                                    }}
                                >
                                    <FaEnvelope />
                                    <span>{t('nav.requestQuote')}</span>
                                </a>
                                <a href="mailto:sales@vsgrill.pl" className="px-6 py-3 rounded-lg border-2 border-vsred text-vsred-light font-semibold hover:bg-vsred hover:text-white transition-all duration-300 text-center">
                                    {t('productDetail.contactSales')}
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>



            {/* Specifications */}
            <section className="py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Technical Specs */}
                        <div>
                            <h2 className="text-3xl font-bold font-serif mb-6 text-vsred">
                                {t('productDetail.specifications')}
                            </h2>
                            <div className="card-dark p-6">
                                <table className="w-full">
                                    <tbody>
                                        {Object.entries(product.specifications).map(([key, value], index) => (
                                            <tr key={index} className="border-b border-gray-800/50 last:border-0">
                                                <td className="py-3 text-gray-400 font-medium">{key}</td>
                                                <td className="py-3 text-white text-right font-semibold">{value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Packaging Options */}
                        <div>
                            <h2 className="text-3xl font-bold font-serif mb-6 text-vsred">
                                {t('productDetail.packaging')}
                            </h2>
                            <div className="space-y-4">
                                {product.packaging.map((option, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: 20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        className="flex items-center gap-3 bg-vsblackLight border border-gray-800/50 rounded-lg p-4"
                                    >
                                        <FaBoxOpen className="text-vsred text-2xl flex-shrink-0" />
                                        <span className="text-gray-300">{option}</span>
                                    </motion.div>
                                ))}
                            </div>

                            <div className="mt-6 card-dark p-6">
                                <div className="flex items-start gap-3 mb-4">
                                    <FaShippingFast className="text-vsred text-2xl mt-1" />
                                    <div>
                                        <h3 className="font-bold text-white mb-2">{t('productDetail.shipping.title')}</h3>
                                        <p className="text-sm text-gray-400">
                                            {t('productDetail.shipping.description')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="card-dark p-12 text-center"
                    >
                        <h2 className="text-3xl font-bold font-serif mb-4 text-white">
                            {t('productDetail.readyToOrder')} {product.name}?
                        </h2>
                        <p className="text-gray-400 mb-8 text-lg">
                            {t('productDetail.quoteDescription')}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a
                                href="/#quote"
                                className="btn-primary inline-flex items-center gap-2"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/');
                                    setTimeout(() => {
                                        document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' });
                                    }, 100);
                                }}
                            >
                                <FaEnvelope />
                                <span>{t('productDetail.detailedQuote')}</span>
                            </a>
                            <Link to="/products" className="px-6 py-3 rounded-lg border-2 border-gray-700 text-gray-300 font-semibold hover:border-vsred hover:text-vsred-light transition-all duration-300">
                                {t('productDetail.viewOtherProducts')}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </main>
    );
}

